'use client'

import * as React from 'react'
import { cn } from '../../utils/cn'
import { Section } from '../layout/Section'
import { Container } from '../layout/Container'
import { FadeIn } from '../motion/FadeIn'
import { StaggerChildren } from '../motion/StaggerChildren'

interface Step {
  number: string
  title: string
  description: string
  icon?: React.ReactNode
}

interface HowItWorksProps {
  sectionNumber?: string
  heading: string
  subheading?: string
  steps: Step[]
}

export function HowItWorks({
  sectionNumber,
  heading,
  subheading,
  steps
}: HowItWorksProps) {
  return (
    <Section number={sectionNumber}>
      <Container>
        {/* Section Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <FadeIn>
            <h2 className="headline-lg mb-4">{heading}</h2>
          </FadeIn>
          {subheading && (
            <FadeIn delay={0.1}>
              <p className="body-lg">{subheading}</p>
            </FadeIn>
          )}
        </div>

        {/* Steps */}
        <StaggerChildren
          className="relative"
          staggerDelay={0.15}
        >
          {/* Connecting line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-[var(--landing-border)] -translate-x-1/2 hidden md:block" />

          <div className="space-y-12 md:space-y-0">
            {steps.map((step, index) => (
              <div
                key={index}
                className={cn(
                  'relative grid md:grid-cols-2 gap-8 md:gap-16 items-center',
                  index % 2 === 1 && 'md:[&>*:first-child]:order-2'
                )}
              >
                {/* Content */}
                <div className={cn(
                  'md:text-right',
                  index % 2 === 1 && 'md:text-left'
                )}>
                  <div className={cn(
                    'inline-flex items-center gap-4 mb-4',
                    index % 2 === 0 ? 'md:flex-row-reverse' : ''
                  )}>
                    {step.icon && (
                      <div className="w-10 h-10 rounded-lg bg-[var(--accent)]/10 flex items-center justify-center text-[var(--accent)]">
                        {step.icon}
                      </div>
                    )}
                    <span className="text-sm font-semibold text-[var(--accent)]">
                      Step {step.number}
                    </span>
                  </div>

                  <h3 className="headline-md mb-3">
                    {step.title}
                  </h3>

                  <p className="body-md">
                    {step.description}
                  </p>
                </div>

                {/* Number indicator (center) */}
                <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-[var(--landing-bg)] border-2 border-[var(--accent)] flex items-center justify-center">
                    <span className="text-lg font-bold text-[var(--accent)]">
                      {step.number}
                    </span>
                  </div>
                </div>

                {/* Placeholder for grid alignment */}
                <div className="hidden md:block" />
              </div>
            ))}
          </div>
        </StaggerChildren>
      </Container>
    </Section>
  )
}
